import { useMemo } from "react";

import { useCurrentLocation, useOrgs, useSecureImageSrc } from "~/hooks";
import { getOrgShortNameLower } from "~/utils/Helper";
import { LooseObject } from "~/utils/Types";
import { toImageApiUrl, toThumbUrl } from "~/utils/imageUrls";

import useCameraUserSettingsQuery from "./useCameraUserSettingsQuery";

/**
 * Resolves a camera image row to a displayable `src`.
 *
 * The blob path segment is `PHOTO_DATETIME` regardless of which column the
 * caller renders as its date, so that value is read off the row directly rather
 * than through a caller-supplied key.
 *
 * Tiles render at a few hundred px, so the download URL defaults to the 640px
 * `/thumb` variant (INS-1559).
 */
const useCameraImageSrc = ({
  item,
  imgUrlKey,
  isThumbnail = true,
  keyframeHashKey,
  locationKey,
}: {
  item: LooseObject;
  imgUrlKey: string;
  /** Clear it where the image is shown large enough for 640px to look soft. */
  isThumbnail?: boolean;
  keyframeHashKey: string;
  locationKey: string;
}) => {
  const org = useOrgs().currentOrg;
  const location = useCurrentLocation();
  const cameraUserSettingsQuery = useCameraUserSettingsQuery();

  const [orgSection, locationSection] = useMemo(
    () =>
      org
        ? [getOrgShortNameLower(org.idString), item[locationKey]]
        : [null, null],
    [org, item, locationKey],
  );
  // Consumers that resolve images by URL rather than by keyframe hash pass
  // rows with no PHOTO_DATETIME at all; downloadUrl's own guard already
  // handles the resulting undefined.
  const photoDateSection = useMemo(
    () => item.PHOTO_DATETIME?.substring(0, 10),
    [item.PHOTO_DATETIME],
  );
  const keySection = item[keyframeHashKey];

  const downloadUrl = useMemo(() => {
    if (!orgSection || !locationSection || !photoDateSection || !keySection) {
      return null;
    }
    const viewingLocation = location.toLowerCase();
    // The image API answers a shared image at the viewing place, then serves
    // the source place's image only if the viewing place's own share names it.
    const share =
      viewingLocation === locationSection
        ? undefined
        : cameraUserSettingsQuery.data?.crossOrgShare?.find(
            (candidate) => candidate.location.toLowerCase() === locationSection,
          );
    const placeSection = share
      ? `${orgSection}/${viewingLocation}/shared/${share.org.toLowerCase()}/${locationSection}`
      : `${orgSection}/${locationSection}`;
    return toImageApiUrl(
      `/image/download/${placeSection}/${photoDateSection}/${keySection}`,
    );
  }, [
    orgSection,
    locationSection,
    photoDateSection,
    keySection,
    location,
    cameraUserSettingsQuery.data,
  ]);

  const demoUrl = useMemo(
    () => toImageApiUrl(item[imgUrlKey]),
    [item, imgUrlKey],
  );

  return useSecureImageSrc({
    downloadUrl: isThumbnail ? toThumbUrl(downloadUrl) : downloadUrl,
    demoUrl: isThumbnail ? toThumbUrl(demoUrl) : demoUrl,
  });
};

export default useCameraImageSrc;
